// eslint-disable-next-line import/no-named-as-default
import gsap, { Elastic } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

import BasePage from '../../components/base-page/base-page';
import Carousel from '../../components/carousel/carousel';
import VideoModal from '../../components/video-modal/video-modal';

import breakpoints from '../../config/breakpoints';

import './home.scss';

gsap.registerPlugin(ScrollTrigger);

class HomePage extends BasePage {
  ui = {
    headerMacpawBadge: '.js-header-macpaw-badge',
    howToUseBlock: '.js-how-to-use-block',
    testimonialsCarousel: '.js-testimonials-carousel',
    videoTestimonialsCarousel: '.js-video-testimonials-carousel',
    promoAppsSection: '.js-promo-apps-section',
    promoAppsSelect: '.js-promo-apps-select',
  };

  uiCollections = {
    headerBadges: '.js-header-badge',
    howToUseVideoContainers: '.js-how-to-use-video-container',
    howToUseVideos: '.js-how-to-use-video',
    promoAppsCollections: '.js-promo-apps-collection',
    promoAppsRadioSwitcher: '.js-promo-apps-radio-switcher',
  };

  events = {
    'mouseenter @uiCollections.headerBadges': 'handleBadgeMouseEnver',
    'mouseenter @ui.headerMacpawBadge': 'handleMacpawBadgeMouseEnver',
    'mouseleave @uiCollections.headerBadges': 'handleBadgeMouseLeave',
    'ended @uiCollections.howToUseVideos': 'handleHowToUseVideoEnd',
    'mouseenter @uiCollections.howToUseVideos': 'handleHowToUseVideoHover',
    'click @uiCollections.promoAppsRadioSwitcher': 'handlePromoAppCollectionSelect',
    'change @ui.promoAppsSelect': 'handlePromoAppCollectionSelect',
  };

  collectionsBgClasses = {
    essentials: 'promo-apps_bg-green',
    getThingsDone: 'promo-apps_bg-blue',
    browsingSecurity: 'promo-apps_bg-purple',
    defaultAppAlternatives: 'promo-apps_bg-dirty-purple',
  };

  howToUseVideoContainerPlayingClass = 'how-to-use__step-video-container_playing';

  onInit() {
    super.onInit();

    this.testimonialsCarousel = new Carousel({
      rootElement: this.ui.testimonialsCarousel,
      slidesPerPage: {
        [breakpoints.sm]: 2,
        [breakpoints.lg]: 3,
      },
    });
    this.testimonialsCarousel.init();

    this.videoTestimonialsCarousel = new Carousel({
      rootElement: this.ui.videoTestimonialsCarousel,
    });
    this.videoTestimonialsCarousel.init();

    this.videoModal = new VideoModal();
    this.videoModal.init();

    ScrollTrigger.create({
      trigger: this.ui.howToUseBlock,
      start: 'top bottom',
      end: 'bottom',
      onToggle: this.toggleHowToUseAnimation,
    });
  }

  handleBadgeMouseEnver = ({ target }) => {
    gsap.to(target, {
      rotate: target.dataset.rotateByHoverTo,
      scale: 1.1,
      duration: 0.8,
      ease: Elastic.easeOut.config(1, 0.3),
    });
  }

  handleBadgeMouseLeave = ({ target }) => {
    gsap.to(target, {
      rotate: 0,
      scale: 1,
      duration: 0.8,
      ease: Elastic.easeOut.config(1, 0.6),
    });
  }

  handleMacpawBadgeMouseEnver = ({ target }) => {
    if (!gsap.isTweening(target)) {
      gsap.to(target, {
        rotate: '+=360',
        duration: 0.8,
        ease: 'Power1.easeInOut',
      });
    }
  }

  toggleHowToUseAnimation = () => {
    if (this.currentHowToUseVideoId === undefined) this.playHowToUseVideoById(0);
    else this.stopCurrentHowToUseVideo();
  }

  playHowToUseVideoById = async (videoId) => {
    await this.uiCollections.howToUseVideos[videoId].play();
    this.currentHowToUseVideoId = videoId;
    this.uiCollections.howToUseVideoContainers[videoId].classList.add(this.howToUseVideoContainerPlayingClass);
  }

  stopCurrentHowToUseVideo = () => {
    if (this.currentHowToUseVideoId !== undefined) {
      this.uiCollections.howToUseVideos[this.currentHowToUseVideoId].pause();
      // eslint-disable-next-line no-param-reassign
      this.uiCollections.howToUseVideos[this.currentHowToUseVideoId].currentTime = 0;
      this.uiCollections.howToUseVideoContainers[this.currentHowToUseVideoId]
        .classList.remove(this.howToUseVideoContainerPlayingClass);

      this.currentHowToUseVideoId = undefined;
    }
  }

  handleHowToUseVideoHover = ({ currentTarget }) => {
    const videoId = currentTarget.dataset.orderId - 1;

    if (this.currentHowToUseVideoId === videoId || this.currentHowToUseVideoId === undefined) return;

    this.stopCurrentHowToUseVideo();
    this.playHowToUseVideoById(videoId);
  }

  handleHowToUseVideoEnd = () => {
    const nextVideoId = this.currentHowToUseVideoId < this.uiCollections.howToUseVideos.length - 1
      ? this.currentHowToUseVideoId + 1 : 0;

    this.stopCurrentHowToUseVideo();
    this.playHowToUseVideoById(nextVideoId);
  }

  handlePromoAppCollectionSelect(e) {
    this.selectActivePromoCollection(e.currentTarget.value);
  }

  selectActivePromoCollection(collectionName) {
    // keep radio buttons and select in sync
    this.ui.promoAppsSelect.value = collectionName;
    this.uiCollections.promoAppsRadioSwitcher.forEach((radioButton) => {
      // eslint-disable-next-line no-param-reassign
      radioButton.checked = radioButton.value === collectionName;
    });

    const bgClasses = Object.keys(this.collectionsBgClasses)
      .map((key) => this.collectionsBgClasses[key]);
    this.ui.promoAppsSection.classList.remove(...bgClasses);
    this.ui.promoAppsSection.classList.add(this.collectionsBgClasses[collectionName]);

    this.uiCollections.promoAppsCollections
      .forEach((collection) => collection.classList.toggle('d-none', collection.dataset.collection !== collectionName));
  }
}

export default HomePage;

HomePage.initOnDomLoaded();
